import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { menusGetAllMine } from "~/api-refs";
import { MenuPageHeader } from "~/components/menus/menu-header";
import { MenuSelector } from "~/components/menus/menu-selector";
import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/menus/$menuId")({
  component: MenuLayout,
});

function MenuLayout() {
  const { menuId } = Route.useParams();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canEditMenus = hasPermission("editMenus");

  const { data: menus } = useQuery(
    convexQuery(menusGetAllMine, {
      paginationOpts: {
        numItems: 100,
        cursor: null,
      },
    })
  );

  const selectedMenu = menus?.page.find((m) => m.id === menuId);

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title={selectedMenu?.name ?? "Menu"}
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title={selectedMenu?.name ?? "Menu"}
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to edit menus
  if (!canEditMenus) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title={selectedMenu?.name ?? "Menu"}
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to manage menus. Contact your manager to
            request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="Manage menu items, categories, and modifiers"
      title={selectedMenu?.name ?? "Menu"}
    >
      <div className="space-y-6">
        <MenuSelector menus={menus?.page ?? []} />
        <MenuPageHeader menuId={menuId} />
        <Outlet />
      </div>
    </PageWrapper>
  );
}
